import { TFunction } from 'i18next';
import * as Yup from 'yup';
import {
    JobStepObjectFields,
    SAVE_JOB_FORM_VALUES,
    JobQuestionObjectFields,
    QuestionType,
} from '../interfaces';

const isBandDealbreakerType = (type: string | undefined) =>
    type === QuestionType.NUMERIC ||
    type === QuestionType.SELECT_DATE ||
    type === QuestionType.DATE_RANGE;

export const saveJobSchema = (t: TFunction) => {
    return Yup.object().shape({
        [SAVE_JOB_FORM_VALUES.TITLE]: Yup.string()
            .required(t('saveJob.titleRequired'))
            .max(120, t('saveJob.titleMaxLength')),
        [SAVE_JOB_FORM_VALUES.LOCATION]: Yup.string().notRequired(),
        [SAVE_JOB_FORM_VALUES.EXTERNAL_JOB_ID]: Yup.string().notRequired(),
        [SAVE_JOB_FORM_VALUES.STEPS_SELECTED_JOB]: Yup.object().notRequired(),
        [SAVE_JOB_FORM_VALUES.STEPS]: Yup.array().of(
            Yup.object().shape({
                [JobStepObjectFields.ID]: Yup.string().required(),
                [JobStepObjectFields.STEP_NAME]: Yup.string().required(),
                [JobStepObjectFields.IS_FIRST_STEP]: Yup.boolean(),
                [JobStepObjectFields.IS_LAST_STEP]: Yup.boolean(),
                [JobStepObjectFields.STEP_OWNER]: Yup.object()
                    .nullable()
                    .when(
                        [
                            JobStepObjectFields.IS_FIRST_STEP,
                            JobStepObjectFields.IS_LAST_STEP,
                        ],
                        {
                            is: (isFirst?: boolean, isLast?: boolean) =>
                                !isFirst && !isLast,
                            then: (schema) => schema.required(),
                            otherwise: (schema) => schema.notRequired(),
                        }
                    ),
            })
        ),
        [SAVE_JOB_FORM_VALUES.QUESTIONS_SELECTED_JOB]:
            Yup.object().notRequired(),
        [SAVE_JOB_FORM_VALUES.QUESTIONS]: Yup.array().of(
            Yup.object().shape({
                [JobQuestionObjectFields.ID]: Yup.string().required(),
                [JobQuestionObjectFields.QUESTION_TITLE]: Yup.string()
                    .required(t('saveJob.questionTitleRequired'))
                    .max(255, t('saveJob.questionTitleMaxLength')),
                [JobQuestionObjectFields.QUESTION_TYPE]: Yup.object()
                    .nullable()
                    .required(t('saveJob.questionTypeRequired'))
                    .test(
                        'is-not-null',
                        t('saveJob.questionTypeRequired'),
                        (value) => value !== null && value !== undefined
                    ),
                [JobQuestionObjectFields.IS_REQUIRED]: Yup.boolean(),
                [JobQuestionObjectFields.IS_DEALBREAKER]: Yup.boolean(),
                [JobQuestionObjectFields.OPTIONS]: Yup.array()
                    .of(
                        Yup.object().shape({
                            value: Yup.string().required(),
                            label: Yup.string().required(
                                t('saveJob.optionValueRequired')
                            ),
                            isCorrectAnswer: Yup.boolean(),
                        })
                    )
                    .when(
                        [
                            JobQuestionObjectFields.QUESTION_TYPE,
                            JobQuestionObjectFields.IS_DEALBREAKER,
                        ],
                        {
                            is: (
                                questionType: { value: string } | null,
                                _isDealbreaker: boolean
                            ) => {
                                const type = questionType?.value;
                                return (
                                    type === QuestionType.SINGLE_SELECT ||
                                    type === QuestionType.MULTIPLE_SELECT
                                );
                            },
                            then: (schema) =>
                                schema
                                    .min(1, t('saveJob.optionsRequired'))
                                    .test(
                                        'dealbreaker-option-selected',
                                        t('saveJob.dealbreakerAnswerRequired'),
                                        function (options) {
                                            const { parent } = this;
                                            const isDealbreaker =
                                                parent[
                                                    JobQuestionObjectFields
                                                        .IS_DEALBREAKER
                                                ];
                                            if (!isDealbreaker) return true;
                                            return (
                                                options?.some(
                                                    (opt: {
                                                        isCorrectAnswer?: boolean;
                                                    }) => opt.isCorrectAnswer
                                                ) || false
                                            );
                                        }
                                    ),
                            otherwise: (schema) => schema,
                        }
                    ),
                [JobQuestionObjectFields.DEALBREAKER_ANSWER]: Yup.string()
                    .nullable()
                    .when(
                        [
                            JobQuestionObjectFields.QUESTION_TYPE,
                            JobQuestionObjectFields.IS_DEALBREAKER,
                        ],
                        {
                            is: (
                                questionType: { value: string } | null,
                                isDealbreaker: boolean
                            ) =>
                                questionType?.value === QuestionType.BOOLEAN &&
                                isDealbreaker,
                            then: (schema) =>
                                schema
                                    .required(
                                        t('saveJob.dealbreakerAnswerRequired')
                                    )
                                    .oneOf(
                                        ['true', 'false'],
                                        t('saveJob.dealbreakerAnswerRequired')
                                    ),
                            otherwise: (schema) => schema,
                        }
                    ),
                [JobQuestionObjectFields.BAND_FROM]: Yup.string()
                    .nullable()
                    .when(
                        [
                            JobQuestionObjectFields.QUESTION_TYPE,
                            JobQuestionObjectFields.IS_DEALBREAKER,
                        ],
                        {
                            is: (
                                questionType: { value: string } | null,
                                isDealbreaker: boolean
                            ) =>
                                questionType?.value === QuestionType.NUMERIC &&
                                isDealbreaker,
                            then: (schema) =>
                                schema
                                    .required(
                                        t('saveJob.numericMinValueRequired')
                                    )
                                    .test(
                                        'is-valid-number',
                                        t('saveJob.numericMinValueInvalid'),
                                        (value) => {
                                            if (!value) return false;
                                            const num = parseFloat(
                                                value.replace(/,/g, '')
                                            );
                                            return !isNaN(num);
                                        }
                                    ),
                            otherwise: (schema) => schema,
                        }
                    ),
                [JobQuestionObjectFields.BAND_TO]: Yup.string()
                    .nullable()
                    .when(
                        [
                            JobQuestionObjectFields.QUESTION_TYPE,
                            JobQuestionObjectFields.IS_DEALBREAKER,
                            JobQuestionObjectFields.BAND_FROM,
                        ],
                        {
                            is: (
                                questionType: { value: string } | null,
                                isDealbreaker: boolean
                            ) =>
                                questionType?.value === QuestionType.NUMERIC &&
                                isDealbreaker,
                            then: (schema) =>
                                schema
                                    .required(
                                        t('saveJob.numericMaxValueRequired')
                                    )
                                    .test(
                                        'is-valid-number',
                                        t('saveJob.numericMaxValueInvalid'),
                                        (value) => {
                                            if (!value) return false;
                                            const num = parseFloat(
                                                value.replace(/,/g, '')
                                            );
                                            return !isNaN(num);
                                        }
                                    )
                                    .test(
                                        'is-greater-than-min',
                                        t(
                                            'saveJob.numericMaxValueMustBeGreater'
                                        ),
                                        function (value) {
                                            const { parent } = this;
                                            const minValue =
                                                parent[
                                                    JobQuestionObjectFields
                                                        .BAND_FROM
                                                ];
                                            if (!value || !minValue)
                                                return true;
                                            const min = parseFloat(
                                                minValue.replace(/,/g, '')
                                            );
                                            const max = parseFloat(
                                                value.replace(/,/g, '')
                                            );
                                            return max >= min;
                                        }
                                    ),
                            otherwise: (schema) => schema,
                        }
                    ),
                [JobQuestionObjectFields.DATE_FROM]: Yup.mixed()
                    .nullable()
                    .when(
                        [
                            JobQuestionObjectFields.QUESTION_TYPE,
                            JobQuestionObjectFields.IS_DEALBREAKER,
                        ],
                        {
                            is: (
                                questionType: { value: string } | null,
                                isDealbreaker: boolean
                            ) =>
                                isDealbreaker &&
                                isBandDealbreakerType(questionType?.value) &&
                                questionType?.value !== QuestionType.NUMERIC,
                            then: (schema) =>
                                schema.required(
                                    t('saveJob.dateFromRequired')
                                ),
                            otherwise: (schema) => schema,
                        }
                    ),
                [JobQuestionObjectFields.DATE_TO]: Yup.mixed()
                    .nullable()
                    .when(
                        [
                            JobQuestionObjectFields.QUESTION_TYPE,
                            JobQuestionObjectFields.IS_DEALBREAKER,
                        ],
                        {
                            is: (
                                questionType: { value: string } | null,
                                isDealbreaker: boolean
                            ) =>
                                isDealbreaker &&
                                (questionType?.value ===
                                    QuestionType.SELECT_DATE ||
                                    questionType?.value ===
                                        QuestionType.DATE_RANGE),
                            then: (schema) =>
                                schema
                                    .required(t('saveJob.dateToRequired'))
                                    .test(
                                        'is-after-from',
                                        t('saveJob.dateToMustBeAfterFrom'),
                                        function (value) {
                                            const { parent } = this;
                                            const from =
                                                parent[
                                                    JobQuestionObjectFields
                                                        .DATE_FROM
                                                ];
                                            if (!value || !from) return true;
                                            return (
                                                new Date(value as Date) >=
                                                new Date(from as Date)
                                            );
                                        }
                                    ),
                            otherwise: (schema) => schema,
                        }
                    ),
            })
        ),
    });
};
